import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Path, PathValue, useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import useFiltersCache from './useFiltersCache';
import { URLParam } from '../Interfaces/system/params.interfaces';
import { readParam } from '@/src/utils/param';

type useParamsProps<T extends Record<string, unknown>> = {
  params: URLParam<T>[];
  extraParams?: { label: string; change: (value?: string) => void }[];
  formatFilters?: (filters: Partial<T>) => Record<string, unknown>;
  tab: string;
  filterChanges?: (cookies: unknown) => unknown;
  afterReset?: () => void;
  cookieFiltersName: string;
  cookiePath?: string;
};

const useParamsAndCookies = <T extends Record<string, unknown>>({
  tab,
  formatFilters,
  params,
  extraParams,
  filterChanges,
  afterReset,
  cookieFiltersName,
  cookiePath,
}: useParamsProps<T>) => {
  const [cookies] = useCookies([cookieFiltersName]);
  const [searchParams, setSearchParams] = useSearchParams();
  const { setValue, reset } = useFormContext<T>();
  const filters = useWatch<T>();
  useFiltersCache({
    tab,
    filters: formatFilters ? formatFilters(filters as Partial<T>) : filters,
    cookieName: cookieFiltersName,
    path: cookiePath,
  });
  const [rendered, setRendered] = useState(false);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    let hasParam = false;

    extraParams?.forEach((param) => {
      let value = newSearchParams.get(param.label);
      if (value) {
        if (!hasParam) {
          hasParam = true;
          reset();
          if (afterReset) {
            afterReset();
          }
        }

        param.change(value);
        newSearchParams.delete(param.label);
      }
    });

    params.forEach((param) => {
      const value = readParam(newSearchParams, param);
      if (value === undefined) {
        return;
      }

      if (!hasParam) {
        hasParam = true;
        reset();
        if (afterReset) {
          afterReset();
        }
      }

      newSearchParams.delete(String(param.label));
      setValue(param.label as Path<T>, value as PathValue<T, Path<T>>);
    });

    if (hasParam) {
      setSearchParams(newSearchParams);
      setRendered(true);
    } else if (!rendered && cookies[cookieFiltersName]?.tab === tab) {
      setRendered(true);
      let cookieFilters = cookies[cookieFiltersName]?.filters;

      if (filterChanges) {
        cookieFilters = filterChanges(cookieFilters);
      }

      for (const i in cookieFilters) {
        setValue(i as Path<T>, cookieFilters[i]);
      }
    }
  }, [searchParams, rendered, cookies[cookieFiltersName]]);
};

export default useParamsAndCookies;
